import * as moment from 'moment';

import { UnexpectedArgument } from 'src/app/shared/errors/unexpected-argument.error';
import { VisibiliteEnum } from 'src/app/data/edition/models/enums/visibilite.enum';

/**
 * Datetime helper - moment <-> string (ISO-like)
 */
export class DateTimeConverter {
    deserialize(data: string): moment.Moment {
        if (data === null || data === undefined) {
            return data as any;
        }
        const m = moment(data);
        if (m.isValid()) {
            return m;
        } else {
            throw new UnexpectedArgument('invalid date representation', data);
        }
    }

    serialize(data: moment.Moment): string {
        return data && (data as any).format ? (data as any).format('YYYY-MM-DDTHH:mm:ss.SSSZZ') : null;
    }
}

/**
 * Date helper - moment <-> string (YYYY-MM-DD)
 */
export class DateConverter {
    deserialize(data: string): moment.Moment {
        if (data === null || data === undefined) {
            return data as any;
        }
        const m = moment(data);
        if (m.isValid()) {
            return m;
        } else {
            throw new UnexpectedArgument('invalid date representation', data);
        }
    }

    serialize(data: moment.Moment): string {
        return data && (data as any).format ? (data as any).format('YYYY-MM-DD') : null;
    }
}

/**
 * Native Date helper - Date <-> string (YYYY-MM-DD)
 */
export class NativeDateConverter {
    deserialize(data: string | Date): Date {
        if (data === null || data === undefined) {
            return data as any;
        }
        if (data instanceof Date) {
            return data;
        }
        // Expecting an ISO-like date string; let Date parse it
        const d = new Date(data);
        if (!isNaN(d.getTime())) {
            return d;
        }
        throw new UnexpectedArgument('invalid date representation', data as any);
    }

    serialize(data: Date): string {
        if (data === null || data === undefined) {
            return data as any;
        }
        if (!(data instanceof Date) || isNaN(data.getTime())) {
            return null as any;
        }
        // Format as YYYY-MM-DD
        const year = data.getFullYear();
        const month = (data.getMonth() + 1).toString().padStart(2, '0');
        const day = data.getDate().toString().padStart(2, '0');
        return `${year}-${month}-${day}`;
    }
}

export class VisibiliteEnumConverter {
    deserialize(obj: string): VisibiliteEnum {
        return obj as VisibiliteEnum;
    }

    serialize(obj: VisibiliteEnum): string {
        return VisibiliteEnum[obj].toString();
    }
}

/**
 * Converter for plain object <-> Map<number, number>
 * JSON is expected as a plain object with numeric (or numeric-string) keys and number values.
 */
export class MapNumberNumberConverter {
    deserialize(data: any): Map<number, number> {
        if (data === null || data === undefined) {
            return data as any;
        }
        const map = new Map<number, number>();
        Object.keys(data).forEach(k => {
            const keyNum = Number(k);
            const val = (data as any)[k];
            if (!Number.isNaN(keyNum)) {
                map.set(keyNum, typeof val === 'number' ? val : Number(val));
            }
        });
        return map;
    }

    serialize(data: Map<number, number>): any {
        if (data === null || data === undefined) {
            return data as any;
        }
        const out: any = {};
        data.forEach((v, k) => {
            out[String(k)] = v;
        });
        return out;
    }
}

/**
 * Converter for plain object <-> Map<string, number>
 * JSON is expected as a plain object with string keys and number values (or numeric strings).
 */
export class MapStringNumberConverter {
    deserialize(data: any): Map<string, number> {
        if (data === null || data === undefined) {
            return data as any;
        }
        const map = new Map<string, number>();
        Object.keys(data).forEach(k => {
            const val = (data as any)[k];
            map.set(k, typeof val === 'number' ? val : Number(val));
        });
        return map;
    }

    serialize(data: Map<string, number>): any {
        if (data === null || data === undefined) {
            return data as any;
        }
        const out: any = {};
        data.forEach((v, k) => {
            out[k] = v;
        });
        return out;
    }
}
